import React from 'react';

import DashboardInsights from '../../pages/components/DashboardInsights';
import DashboardVisualizations from '../../pages/components/DashboardVisualizations';
import SliderChart from '../../pages/components/SliderChart';
import RadarChart from '../../pages/graphs/dashboard-graph';
import type { DashboardAssessmentData } from '../../utils/dashboardAssessmentAdapter';
import { ClientDashboardPreviewPlaceholder } from './ClientDashboardPreviewPlaceholder';

export type ClientDashboardAnalyticsSectionProps = {
  mode: 'preview' | 'live';
  assessmentData: DashboardAssessmentData | null;
  loading: boolean;
  error: string | null;
  showInsights: boolean;
  showRadar: boolean;
  showSlider: boolean;
  showWordCloud: boolean;
};

/**
 * Unified maturity / chart zone — preview ghosts before ratings,
 * live charts in the same bento layout after assessment data exists.
 */
export function ClientDashboardAnalyticsSection({
  mode,
  assessmentData,
  loading,
  error,
  showInsights,
  showRadar,
  showSlider,
  showWordCloud,
}: ClientDashboardAnalyticsSectionProps) {
  if (mode === 'preview') {
    return <ClientDashboardPreviewPlaceholder />;
  }

  const hasAnyChart =
    showInsights || showRadar || showSlider || showWordCloud;

  if (!hasAnyChart) {
    return null;
  }

  return (
    <div className="client-dash__analytics-bento client-dash__analytics-bento--live">
      {showInsights ? (
        <div className="client-dash__analytics-slot client-dash__analytics-slot--insights">
          <DashboardInsights
            assessmentData={assessmentData}
            loading={loading}
            error={error}
          />
        </div>
      ) : null}

      {(showRadar || showSlider) && (
        <div className="client-dash__analytics-row">
          {showRadar ? (
            <div
              className="client-dash__analytics-slot client-dash__analytics-slot--radar client-dash__analytics-card"
              aria-label="Capability radar"
            >
              <RadarChart
                assessmentData={assessmentData}
                loading={loading}
                error={error}
              />
            </div>
          ) : null}
          {showSlider ? (
            <div
              className="client-dash__analytics-slot client-dash__analytics-slot--slider client-dash__analytics-card"
              aria-label="Capability scores"
            >
              <SliderChart
                assessmentData={assessmentData}
                loading={loading}
                error={error}
              />
            </div>
          ) : null}
        </div>
      )}

      {showWordCloud ? (
        <div
          className="client-dash__analytics-slot client-dash__analytics-slot--cloud client-dash__analytics-card"
          aria-label="Assessment themes"
        >
          <DashboardVisualizations
            assessmentData={assessmentData}
            loading={loading}
            error={error}
          />
        </div>
      ) : null}
    </div>
  );
}

export default ClientDashboardAnalyticsSection;
